import { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

import "@react-pdf-viewer/core/lib/styles/index.css";

// Import the zoom plugin
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

// CROPPER
// import ImageCropper from "../utils/ImageCropper";
import ImageCropper from "../utils/ImageCropper";


pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;



const MultiFileUploader = ({ formData, setFormData, onImageChange, title }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customNames, setCustomNames] = useState({});
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);

  // Initialize zoom plugin for PDF viewer
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  // IMAGE CROPPER
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [imageToCropIndex, setImageToCropIndex] = useState(null);
  



  useEffect(() => {
    if (formData?.uploadedFiles?.length > 0) {
      setUploadedFiles(formData.uploadedFiles);
    }
  }, [formData.uploadedFiles]);

  // List available video input devices (cameras)
  useEffect(() => {
    async function getVideoDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === "videoinput");
        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId(videoDevices[0].deviceId);
        }
      } catch (error) {
        console.error("Error getting video devices", error);
        Swal.fire("Error", "Unable to access cameras on this device.", "error");
      }
    }
    getVideoDevices();
  }, []);

  // Start video stream for selected camera
  useEffect(() => {
    async function startStream() {
      if (selectedDeviceId) {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: selectedDeviceId } },
          });
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        } catch (error) {
          console.error("Error accessing camera", error);
          Swal.fire("Error", "Unable to access the selected camera.", "error");
        }
      }
    }
    startStream();

    // Cleanup on unmount or when device changes
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [selectedDeviceId]);

  const handleMultipleFiles = async (e) => {
    const files = Array.from(e.target.files);
    const allowedFiles = ["application/pdf", "image/jpeg", "image/png", "image/tiff"];

    const validFiles = files.filter((file) => allowedFiles.includes(file.type));

    if (validFiles.length === 0) {
      Swal.fire("Invalid files", "Only PDF or image files are allowed.", "error");
      return;
    }

    const duplicates = [];
    const nonDuplicateFiles = validFiles.filter((file) => {
      const isDuplicate = uploadedFiles.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified
      );
      if (isDuplicate) {
        duplicates.push(file.name);
        return false;
      }
      return true;
    });

    if (duplicates.length > 0) {
      Swal.fire("Duplicate file(s) skipped", duplicates.join(", "), "warning");
    }

    const validatedFiles = [];

    for (const file of nonDuplicateFiles) {
      if (file.type === "application/pdf") {
        try {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } catch (err) {
          if (err?.name === "PasswordException") {
            Swal.fire(
              "Password Protected",
              `${file.name} is password protected and cannot be uploaded.`,
              "error"
            );
            continue;
          } else {
            Swal.fire("Error", `Failed to load PDF: ${file.name}`, "error");
            continue;
          }
        }
      }
      validatedFiles.push(file);
    }

    setUploadedFiles((prev) => {
      const updated = [...prev, ...validatedFiles];

      const newCustomNames = { ...customNames };
      validatedFiles.forEach((file, idx) => {
        const newIndex = prev.length + idx;
        newCustomNames[newIndex] = file.name.replace(/\.[^/.]+$/, "");
      });

      setCustomNames(newCustomNames);
      onImageChange && onImageChange(updated);
      setFormData((prevForm) => ({
        ...prevForm,
        uploadedFiles: updated,
      }));
      return updated;
    });

    e.target.value = null; // allow re-upload of same file
  };

  // Capture photo from video stream
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        Swal.fire("Error", "Failed to capture image.", "error");
        return;
      }

      // Create a File object from blob
      const file = new File([blob], `captured_${Date.now()}.jpeg`, { type: "image/jpeg" });

      // Check for duplicate
      const isDuplicate = uploadedFiles.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified
      );

      if (isDuplicate) {
        Swal.fire("Duplicate", "This captured image is already uploaded.", "warning");
        return;
      }

      // Add captured photo to uploaded files
      setUploadedFiles((prev) => {
        const updated = [...prev, file];

        const newCustomNames = { ...customNames };
        const newIndex = prev.length;
        newCustomNames[newIndex] = file.name.replace(/\.[^/.]+$/, "");

        setCustomNames(newCustomNames);
        onImageChange && onImageChange(updated);
        setFormData((prevForm) => ({
          ...prevForm,
          uploadedFiles: updated,
        }));
        return updated;
      });
    }, "image/jpeg");
  };

  const handleNameChange = (index, newBaseName) => {
    const sanitizedBaseName = newBaseName.replace(/[^a-zA-Z0-9_ ]/g, "");
    setCustomNames((prev) => ({
      ...prev,
      [index]: sanitizedBaseName,
    }));
  };

  const removeUploadedFile = (indexToRemove) => {
    setUploadedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== indexToRemove);

      const updatedNames = { ...customNames };
      delete updatedNames[indexToRemove];

      const reindexedNames = {};
      updated.forEach((_, i) => {
        reindexedNames[i] = updatedNames[i >= indexToRemove ? i + 1 : i];
      });

      setCustomNames(reindexedNames);
      onImageChange && onImageChange(updated);
      setFormData((prevForm) => ({
        ...prevForm,
        uploadedFiles: updated,
      }));
      return updated;
    });
  };



  // Cropper Modal Logic
   const openCropModal = (index) => {
    const file = uploadedFiles[index];
    console.log(file);
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setImageToCrop(url);
    setImageToCropIndex(index);
    setCropModalOpen(true);
  };

  const handleCropComplete = (croppedBlob) => {
    if (!croppedBlob || imageToCropIndex === null) return;

    // Create new File from cropped blob
    const file = uploadedFiles[imageToCropIndex];
    const croppedFile = new File([croppedBlob], file.name, { type: "image/jpeg" });

    setUploadedFiles((prev) => {
      const updated = [...prev];
      updated[imageToCropIndex] = croppedFile;

      // Update customNames unchanged
      onImageChange && onImageChange(updated);
      setFormData((prevForm) => ({
        ...prevForm,
        uploadedFiles: updated,
      }));

      return updated;
    });
  };




  return (
    <div className="container mt-4">
      <div className="mb-3 d-flex flex-column">
        <label className="form-label fw-bold">{title}</label>
        <input
          type="file"
          accept=".pdf,image/*"
          multiple
          className="form-control"
          onChange={handleMultipleFiles}
        />
      </div>

      {/* Camera Selection and Preview */}
      {devices.length > 0 && (
        <div className="mb-4">
          <label className="form-label fw-bold">OR SCAN DOCUMENTS</label>
          <select
            className="form-select mb-2"
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
          >
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Camera ${device.deviceId}`}
              </option>
            ))}
          </select>

          <div style={{ position: "relative" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", borderRadius: 8, backgroundColor: "#000" }}
            />
            <button
              type="button"
              className="btn btn-primary mt-2 w-100"
              onClick={capturePhoto}
            >
              Capture Document
            </button>
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="mt-4">
          <h5 className="mb-3">Uploaded Files</h5>


          {cropModalOpen && imageToCrop && (
            <ImageCropper
              imageSrc={imageToCrop}
              onClose={() => {
                setCropModalOpen(false);
                URL.revokeObjectURL(imageToCrop);
                setImageToCrop(null);
                setImageToCropIndex(null);
              }}
              onCropComplete={handleCropComplete}
            />
          )}



          <div className="row mb-4" style={{ maxHeight: 500, overflowY: "auto" }}>
            {uploadedFiles.map((file, index) => {
              const previewUrl = URL.createObjectURL(file);
              const isImage = file.type.startsWith("image/");
              const isPDF = file.type === "application/pdf";
              const baseName = customNames[index] || file.name.replace(/\.[^/.]+$/, "");

              return (
                <div key={index} className="col-md-6 mb-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-body d-flex flex-column">
                      <h6 className="card-title">File {index + 1}</h6>

                      {isImage && (
                        <img
                          src={previewUrl}
                          alt={`Preview ${index}`}
                          className="img-fluid mb-2 rounded w-100"
                          style={{ maxHeight: 242, height: 242 }}
                        />
                      )}

                      {isPDF && (
                        <div className="pdf-viewer" style={{ height: 250, maxHeight: 250 }}>
                          <Worker workerUrl="../../js/pdf.worker.min.js">
                            <div className="mb-2 d-flex col-12 justify-content-center p-0">
                              <ZoomOutButton />
                              <ZoomPopover />
                              <ZoomInButton />
                            </div>
                            <Viewer
                              fileUrl={previewUrl}
                              plugins={[zoomPluginInstance]}
                            />
                          </Worker>
                        </div>
                      )}
                      <div className="bg-white" style={{ zIndex: 1 }}>
                        <label className="form-label text-info mb-0">
                          File Name <span className="text-danger fs-5">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control mt-0 mb-2"
                          value={baseName}
                          onChange={(e) => handleNameChange(index, e.target.value)}
                          placeholder="Enter custom file name"
                        />


                        <div className="d-flex flex-row mt-4">

                            <button className={`btn btn-sm btn-danger ${isImage ? 'w-50':'w-100'} mt-0 rounded-0`} onClick={() => removeUploadedFile(index)} >
                              Remove
                            </button>


                            {isImage &&
                              <button className="btn btn-sm btn-warning w-50 mt-0 rounded-0" onClick={() => openCropModal(index)} >
                                Crop
                              </button>}
                        </div>
                     

                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiFileUploader;
